import ApiService from "@/services/ApiService";
import {useAuthStore} from "@/stores/Auth";
import Translator from "@/plugins/Translator";
import {Composer} from "vue-i18n";
import Language from "@/types/Models/Settings/Language";
import IModuleSync from "@/types/Models/Modules/IModuleSync";
import {ModuleList} from "@/models/settings/ModuleList";
import Notifier from "@/plugins/notifications/Notifier";
import DateFormatter from "@/plugins/DateFormatter";
import ModuleSyncError from "@/models/Errors/ModuleSyncError";
import {AxiosHeaders} from "axios";
import {RawAxiosRequestHeaders} from "axios";

/**
 * Parent Klasse für alle Controller
 *
 */
class ControllerBase {
    protected api: ApiService;

    protected authStore = useAuthStore();

    protected translator: Translator;
    protected i18n: Composer<{ message: Language }, Record<"en" | "de", any>>;

    protected moduleId: ModuleList;
    protected moduleRoute: string;

    constructor() {
        this.api = ApiService.getInstance();
        this.translator = Translator.getInstance();
        this.i18n = this.translator.getI18nGlobalInstance();
        this.moduleId = ModuleList.NONE;
        this.moduleRoute = "";
    }

    /**
     * Gibt die Module-Route mit dem Mandanten zurück
     *
     */
    private async getModuleMandant(): Promise<string> {
        const mandant = await this.authStore.getCurrentMandant();
        if (!mandant) return "";
        return `${this.moduleRoute}mandanten/${mandant.mandantId}/`;
    }

    /**
     * Gibt den vollständigen Endpoint zurück. Route darf <b>nicht mit /</b> beginnen
     * @example route: todos - Endpoint: /api/modules/aufgabenplanung/mandanten/70520002/todos
     * @param route
     */
    protected async getModuleMandantRoute(route: string): Promise<string> {
        const base = await this.getModuleMandant();
        return `${base}${route}`;
    }


    /**
     * Gibt den Authorization Header für HTTP Anfragen zurück
     *
     */
    protected async getAuthHeader(): Promise<RawAxiosRequestHeaders | AxiosHeaders> {
        const accessToken = await this.authStore.getAccessToken();
        if (accessToken) {
            return {Authorization: `Bearer ${accessToken}`};
        }
        return {};
    }

    /**
     * Gibt den Auth Header mit Content-Type "multipart/form-data" zurück
     * @protected
     */
    protected async getFormDataHeader(): Promise<RawAxiosRequestHeaders | AxiosHeaders> {
        const header = await this.getAuthHeader();
        header["Content-Type"] = "multipart/form-data";
        return header;
    }

    /*----- Module Sync -----*/
    /**
     * Überprüfen, ob das Modul gerade im Synchronisierungsmodus ist
     *
     * @returns ModuleSyncError bei Fehler
     */
    protected async checkModuleStatus(mandantId: number, moduleId: number) {
        const moduleSync = await this.getModuleSync(mandantId, moduleId);
        /*----- falls ModuleSync nicht existiert -----*/
        if (moduleSync == undefined) {
            const translator = Translator.getInstance();
            const i18n = translator.getI18nGlobalInstance();
            Notifier.showError({message: i18n.t("notifications.update.error")});
            throw new ModuleSyncError("module sync missing");
        }
        /*----- Überprüfen ob Modul synchronisiert wird -----*/
        if (this.isModuleSyncing(moduleSync)) {
            /*----- module is in sync state, cancel action -----*/
            this.showModuleSyncError(moduleSync);
            throw new ModuleSyncError("module is in sync state");
        }
    }

    /**
     * Gibt das IModuleSync Modell zurück
     *
     * @remarks GET: api/mandanten/mandantId/modules/moduleId/sync
     */
    protected async getModuleSync(mandantId: number, moduleId: number): Promise<IModuleSync> {
        const response = await this.api.httpClient.get<IModuleSync>(`mandanten/${mandantId}/modules/${moduleId}/sync`, {
            headers: await this.getAuthHeader(),
        });
        console.log("getModuleSync", response);

        return response.data;
    }

    /**
     * Gibt den bool sync zurück, verwendet für einfachere schreibweise
     *
     */
    protected isModuleSyncing(moduleSync: IModuleSync): boolean {
        return moduleSync.sync;
    }

    /**
     * Toast für Modul Synchronisationsfehler darstellen
     *
     */
    protected async showModuleSyncError(moduleSync: IModuleSync): Promise<void> {
        const translator = Translator.getInstance();
        const i18n = translator.getI18nGlobalInstance();
        return Notifier.showError({
            duration: 8500,
            message: i18n.t("notifications.sync.module.active", {content: DateFormatter.formatDateTime(moduleSync.start)}),
        });
    }

    /**
     * Toast für Datensatz Synchronisationsfehler darstellen
     *
     */
    protected async showEntitySyncError() {
        const translator = Translator.getInstance();
        const i18n = translator.getI18nGlobalInstance();
        return Notifier.showError({
            duration: 8500,
            message: i18n.t("notifications.sync.entity.error"),
        });
    }
}

export default ControllerBase;
